/**
 * Importing ready-made animation files (Mixamo `.fbx`, Meshy/Blender `.glb`) into the library.
 *
 * The file brings its own skeleton, which is auto-mapped and bound exactly like a model's would
 * be, and every frame is then read back through that rig — so an imported clip lands in the same
 * canonical space as a hand-authored one and plays on every character in the library, not just on
 * whichever model happens to be open. Nothing here touches `session.model`: the animation source
 * is a second, throwaway skeleton that never reaches the viewport.
 *
 * Two-step like the library JSON import: `preview` reports what is in the file and what will
 * happen, `commit` bakes it. The loaded three.js objects live in this closure, never in the store
 * (`store.ts`: "no three.js").
 */

import { AppError } from '../../shared';
import type { CanonicalBoneId } from '../../shared';
import {
  defaultClipName,
  importAnimationFile,
  type AnimationSourceClip,
  type ImportedAnimationSource,
} from '../../importExport';
import { autoMapSkeleton, guessWorldCorrection } from '../../rig/mapping';
import { buildBinding, validateMapping } from '../../rig/mappingValidation';
import { DEFAULT_DECIMATION_DEGREES } from '../../rig/keyDecimate';
import { normalizeFps, sampleCanonicalClip } from '../../rig/clipSampler';
import { bindRig } from '../../runtime';
import { clipsRepo } from '../../persistence';
import type { ControllerDeps } from './internal';
import { guard } from './internal';
import { uniqueClipName } from './libraryController';
import type { AnimationImportController, AnimationImportPreview, AnimationImportResult } from './types';

/** What the user asked to bake, per clip. */
export interface AnimationImportSelection {
  index: number;
  name: string;
}

export interface AnimationImportOptions {
  /** Bake rate. Defaults to each clip's own inferred rate. */
  fps?: number;
  /** Angular decimation tolerance in degrees; 0 keeps every sampled frame. */
  toleranceDegrees?: number;
}

export function createAnimationImportController(deps: ControllerDeps): AnimationImportController {
  const { ctx } = deps;
  const { store } = ctx;

  /** The parsed file, held between `preview` and `commit`. Live three.js objects. */
  let source: ImportedAnimationSource | null = null;
  let sourceFileName = '';

  function describeClip(clip: AnimationSourceClip, fileName: string): AnimationSourceClip {
    return { ...clip, name: defaultClipName(fileName, clip.name) };
  }

  const controller: AnimationImportController = {
    async preview(file: File | Blob): Promise<AnimationImportPreview> {
      return guard(ctx, 'The animation file could not be read.', async () => {
        const fileName = file instanceof File ? file.name : 'animation';
        const parsed = await importAnimationFile(file);

        const auto = autoMapSkeleton(parsed.bones);
        const mapping = auto.canonicalToSourceBone;
        const validation = validateMapping(mapping, parsed.bones);

        const missing = validation.errors
          .map((issue) => issue.bone)
          .filter((bone): bone is CanonicalBoneId => bone != null);

        if (!validation.ok) {
          source = null;
          throw new AppError(
            'ANIM_MAPPING_INVALID',
            missing.length > 0
              ? `The skeleton in this file could not be matched to the canonical rig: no bone ` +
                `was found for ${missing.join(', ')}. Import it as a model instead and map the ` +
                'bones by hand.'
              : `The skeleton in this file could not be matched to the canonical rig: ` +
                `${validation.errors[0]?.message ?? 'the mapping is incomplete.'}`,
            { errors: validation.errors },
          );
        }

        source = parsed;
        sourceFileName = fileName;

        return {
          fileName,
          format: parsed.format,
          clips: parsed.clipInfo.map((clip) => describeClip(clip, fileName)),
          mappedBoneCount: Object.keys(mapping).length,
          skinless: parsed.skinless,
          warnings: [...parsed.warnings, ...auto.notes, ...validation.warnings.map((w) => w.message)],
        };
      });
    },

    async commit(
      selection: readonly AnimationImportSelection[],
      options: AnimationImportOptions = {},
    ): Promise<AnimationImportResult> {
      return guard(ctx, 'The animation could not be imported.', async () => {
        const parsed = source;
        if (!parsed) {
          throw new AppError(
            'ANIM_NO_PREVIEW',
            'Choose an animation file before importing; the previous one is no longer loaded.',
          );
        }
        if (selection.length === 0) {
          throw new AppError('ANIM_NOTHING_SELECTED', 'Select at least one animation to import.');
        }

        // One binding for the whole file: the skeleton does not change between its clips.
        const mapping = autoMapSkeleton(parsed.bones).canonicalToSourceBone;
        const binding = buildBinding(
          mapping,
          parsed.bones,
          guessWorldCorrection(parsed.bones, mapping),
        );
        const rig = bindRig(parsed.root, binding);

        const taken = store.getState().library.clips.map((c) => c.name);
        const imported: string[] = [];
        const warnings: string[] = [];
        let sampled = 0;
        let kept = 0;

        for (const entry of selection) {
          const clip = parsed.clips[entry.index];
          if (!clip) continue;

          const name = uniqueClipName(entry.name, taken);
          const result = sampleCanonicalClip(parsed.root, rig, clip, {
            name,
            fps: options.fps ?? parsed.clipInfo[entry.index]?.fps ?? undefined,
            toleranceDegrees: options.toleranceDegrees ?? DEFAULT_DECIMATION_DEGREES,
          });

          await clipsRepo.put(result.clip);
          taken.push(name);
          imported.push(name);
          sampled += result.sampledKeyCount;
          kept += result.keptKeyCount;
          for (const warning of result.warnings) {
            if (!warnings.includes(warning)) warnings.push(warning);
          }
        }

        await deps.controllers.library.refresh();

        store.actions.toast(
          'info',
          imported.length === 1
            ? `"${imported[0]}" imported (${kept} keyframes from ${sampled} sampled).`
            : `${imported.length} animations imported (${kept} keyframes from ${sampled} sampled).`,
        );

        return { imported, sampledKeyCount: sampled, keptKeyCount: kept, warnings };
      });
    },

    cancel(): void {
      source = null;
      sourceFileName = '';
    },

    loadedFileName(): string | null {
      return source ? sourceFileName : null;
    },

    /** The bake rate the UI should offer for a clip, already legal for the library. */
    suggestedFps(clipIndex: number): number {
      return normalizeFps(source?.clipInfo[clipIndex]?.fps ?? null);
    },
  };

  return controller;
}
